import { useCallback, useMemo, useRef, useState } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl';

import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ConversationStarterResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import type { ConversationStarterLabelType } from './ConversationStarterLabelType';
import type { ConversationStarterWithLabel } from './ConversationStarterWithLabel';
import messages from './messages';
import { PageContextType } from './PageContextType';
import { shuffleArray } from './shuffleArray';
import { useGetLLMLabelWithoutRepeat } from './useGetLLMLabelWithoutRepeat';
import { usePageContextType } from './usePageContextType';

const PAGE_TYPE_CONVERSATION_STARTERS: {
	[key in PageContextType]: Array<{
		labelType: ConversationStarterLabelType;
		message: MessageDescriptor;
	}>;
} = {
	[PageContextType.BITBUCKET_PULL_REQUEST]: [
		{ labelType: 'summarize-content', message: messages.bitbucketPullRequestsPrompt1 },
		{ labelType: 'rephrase-content', message: messages.bitbucketPullRequestsPrompt2 },
		{ labelType: 'summarize-content', message: messages.bitbucketPullRequestsPrompt3 },
	],
	[PageContextType.BITBUCKET_REPOSITORY_SOURCE]: [
		{ labelType: 'summarize-content', message: messages.bitbucketRepositorySourcePrompt1 },
		{ labelType: 'find-information', message: messages.bitbucketRepositorySourcePrompt2 },
		{ labelType: 'find-information', message: messages.bitbucketRepositorySourcePrompt3 },
	],
	[PageContextType.BITBUCKET]: [
		{ labelType: 'find-information', message: messages.bitbucketPrompt1 },
		{ labelType: 'find-information', message: messages.bitbucketPrompt4 },
		{ labelType: 'stay-up-to-date', message: messages.bitbucketPrompt3 },
	],
	[PageContextType.CONFLUENCE_HOME]: [
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt1 },
		{ labelType: 'stay-up-to-date', message: messages.confluenceHomePrompt2 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt3 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt4 },
		{ labelType: 'rephrase-content', message: messages.confluenceHomePrompt5 },
		{ labelType: 'draft-content', message: messages.confluenceHomePrompt6 },
		{ labelType: 'find-information', message: messages.confluenceHomePrompt7 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt8 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt9 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt10 },
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt11 },
	],
	[PageContextType.CONFLUENCE_PAGE_VIEW]: [
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt1 },
		{ labelType: 'summarize-content', message: messages.confluencePageViewPrompt2 },
		{ labelType: 'rephrase-content', message: messages.confluencePageViewPrompt3 },
		{ labelType: 'find-people', message: messages.confluencePageViewPrompt4 },
		{ labelType: 'stay-up-to-date', message: messages.confluencePageViewPrompt5 },
		{ labelType: 'be-productive', message: messages.confluencePageViewPrompt6 },
	],
	[PageContextType.CONFLUENCE_WHITEBOARD]: [
		{ labelType: 'summarize-content', message: messages.confluenceWhiteboardPrompt1 },
		{ labelType: 'get-answers', message: messages.confluenceWhiteboardPrompt2 },
		{ labelType: 'find-information', message: messages.confluenceWhiteboardPrompt3 },
		{ labelType: 'be-productive', message: messages.confluenceWhiteboardPrompt4 },
	],
	[PageContextType.CONFLUENCE_PAGE_EDIT]: [
		{ labelType: 'rephrase-content', message: messages.confluencePageEditPrompt1 },
		{ labelType: 'draft-content', message: messages.confluencePageEditPrompt2 },
		{ labelType: 'find-information', message: messages.confluencePageEditPrompt3 },
		{ labelType: 'be-productive', message: messages.confluencePageEditPrompt4 },
	],
	[PageContextType.JIRA_HOME]: [
		{
			labelType: 'stay-up-to-date',
			message: messages.jiraHomePrompt1,
		},
		{
			labelType: 'be-productive',
			message: messages.jiraHomePrompt2,
		},
		{
			labelType: 'find-information',
			message: messages.jiraHomePrompt3,
		},
		{
			labelType: 'summarize-content',
			message: messages.jiraHomePrompt4,
		},
		{ labelType: 'draft-content', message: messages.jiraHomePrompt5 },
		{ labelType: 'be-productive', message: messages.jiraHomePrompt6 },
	],
	[PageContextType.JIRA_ISSUE_VIEW]: [
		{
			labelType: 'find-information',
			message: messages.jiraIssueViewPrompt1,
		},
		{ labelType: 'rephrase-content', message: messages.jiraIssueViewPrompt2 },
		{
			labelType: 'draft-content',
			message: messages.jiraIssueViewPrompt3,
		},
		{ labelType: 'be-productive', message: messages.jiraIssueViewPrompt4 },
	],
	[PageContextType.JIRA_PROJECT]: [
		{ labelType: 'stay-up-to-date', message: messages.jiraProjectPrompt1 },
		{
			labelType: 'be-productive',
			message: messages.jiraProjectPrompt2,
		},
		{
			labelType: 'measure-productivity',
			message: messages.jiraProjectPrompt3,
		},
		{
			labelType: 'find-people',
			message: messages.jiraProjectPrompt4,
		},
		{
			labelType: 'find-people',
			message: messages.jiraProjectPrompt5,
		},
		{
			labelType: 'be-productive',
			message: messages.jiraProjectPrompt6,
		},
		{
			labelType: 'measure-productivity',
			message: messages.jiraProjectPrompt7,
		},
		{ labelType: 'find-people', message: messages.jiraProjectPrompt8 },
		{
			labelType: 'find-information',
			message: messages.jiraProjectPrompt9,
		},
		{
			labelType: 'get-answers',
			message: messages.jiraProjectPrompt10,
		},
		{
			labelType: 'rephrase-content',
			message: messages.jiraProjectPrompt11,
		},
		{
			labelType: 'summarize-content',
			message: messages.jiraProjectPrompt12,
		},
		{
			labelType: 'draft-content',
			message: messages.jiraProjectPrompt13,
		},
		{ labelType: 'be-productive', message: messages.jiraProjectPrompt14 },
	],
	[PageContextType.ATLAS_HOME]: [
		{ labelType: 'find-information', message: messages.atlasHomePrompt1 },
		{ labelType: 'measure-productivity', message: messages.atlasHomePrompt2 },
		{ labelType: 'get-answers', message: messages.atlasHomePrompt3 },
		{ labelType: 'draft-content', message: messages.atlasHomePrompt4 },
		{ labelType: 'be-productive', message: messages.atlasHomePrompt5 },
		{ labelType: 'draft-content', message: messages.atlasHomePrompt6 },
		{ labelType: 'be-productive', message: messages.confluenceHomePrompt1 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt4 },
		{ labelType: 'rephrase-content', message: messages.confluenceHomePrompt5 },
		{ labelType: 'summarize-content', message: messages.confluenceHomePrompt11 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt8 },
		{ labelType: 'find-people', message: messages.confluenceHomePrompt10 },
		{
			labelType: 'stay-up-to-date',
			message: messages.jiraHomePrompt2,
		},
		{
			labelType: 'find-information',
			message: messages.jiraHomePrompt3,
		},
		{
			labelType: 'summarize-content',
			message: messages.jiraHomePrompt4,
		},
		{ labelType: 'be-productive', message: messages.atlasHomePrompt7 },
	],
	[PageContextType.UNKNOWN]: [
		{ labelType: 'find-information', message: messages.unknownPrompt1 },
		{ labelType: 'be-productive', message: messages.unknownPrompt2 },
		{ labelType: 'draft-content', message: messages.unknownPrompt3 },
	],
	[PageContextType.ADMIN_HUB]: [],
};

const getRandomStarters = (starters: ConversationStarterWithLabel[], num: number) => {
	// Return the first `num` items
	return shuffleArray(starters).slice(0, num);
};

const getConversationStarterLimit = (isFullscreen: boolean) => (isFullscreen ? 6 : 3);

// THIS WILL BE DEPRECATED ONCE ALL USERS ARE MIGRATED TO CHAT-REFRESH-V2 (platform/packages/ai-mate/rovo-conversation-actions/src/controllers/rovo-conversation-starters/index.tsx)
export const useContextualConversationStarters: (
	props: Omit<
		AssistanceServiceInjectedParams & {
			onInit?: () => void;
			onSuccess?: () => void;
			onError?: (error: ApiError) => void;
		},
		'assistanceServiceInjectedParams'
	> &
		AssistanceServiceInputParam,
) => {
	getDynamicContextualStarters: (
		payload: {
			fallbackStarters: Array<ConversationStarterWithLabel>;
			userDefinedStarters?: Array<ConversationStarterWithLabel>;
			locationUrl: string;
		},
		callbacks?:
			| {
					onSuccess?: ((data: ConversationStarterResponse) => void) | undefined;
					onError?: (error: ApiError) => void;
			  }
			| undefined,
	) => Promise<ConversationStarterResponse | undefined>;
	starters: (ConversationStarterWithLabel | null)[];
	error: ApiError | null;
	// TODO - Implement appropriate request abort logic for useMutation, much like useStreamingMutation has, this is a stop gap
	isMutating: boolean;
	staticContextualConversationStarters: ConversationStarterWithLabel[];
	cancelTimeout: () => void;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
		onError,
		onSuccess,
		onInit,
	}: AssistanceServiceInjectedParams & {
		onInit?: () => void;
		onSuccess?: () => void;
		onError?: (error: ApiError) => void;
	}) => {
		const { formatMessage } = useIntl();
		const pageContextType = usePageContextType();
		const { fullScreen } = useUIConfig();

		const [didTimeOut, setDidTimeOut] = useState(false);
		const [starters, setStarters] = useState<Array<ConversationStarterWithLabel | null>>(
			new Array(3).fill(null),
		);
		const timeoutRef = useRef<number | null>(null);

		const cancelTimeout = useCallback(() => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		}, []);

		const staticContextualConversationStarters = useMemo(() => {
			const contextualStarters: ConversationStarterWithLabel[] = PAGE_TYPE_CONVERSATION_STARTERS[
				pageContextType
			].map((starter) => {
				return {
					labelType: starter.labelType,
					message: formatMessage(starter.message),
					type: 'static',
				};
			});

			const conversationStarterLimit = getConversationStarterLimit(fullScreen ?? false);

			// We don't need to shuffle the starters when the page context type is unknown
			const staticContextualStarters =
				pageContextType === PageContextType.UNKNOWN
					? contextualStarters.slice(0, conversationStarterLimit)
					: getRandomStarters(contextualStarters, conversationStarterLimit);
			return staticContextualStarters;
		}, [pageContextType, fullScreen, formatMessage]);

		const { getLLMLabel } = useGetLLMLabelWithoutRepeat(pageContextType);

		const {
			mutate: getDynamicContextualStarters,
			error,
			isSubmitting,
		} = useMutation({
			mutationFn: async ({
				fallbackStarters,
				userDefinedStarters = [],
				locationUrl: browser_url,
			}: {
				fallbackStarters: Array<ConversationStarterWithLabel>;
				userDefinedStarters?: Array<ConversationStarterWithLabel>;
				locationUrl: string;
			}) => {
				onInit?.();

				experienceTracker.start({
					id: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
					name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
				});
				setDidTimeOut(false);
				setStarters(
					[
						...userDefinedStarters,
						...fallbackStarters.slice(0, 1),
						...new Array(3).fill(null),
					].slice(0, 3),
				);
				beginTimeout(userDefinedStarters, fallbackStarters);
				return await assistanceService.generateContextualStarters({ browser_url });
			},
			onSuccess: async (responseData) => {
				onSuccess?.();

				experienceTracker.succeed({
					name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
				});

				let updatedStarters = [...starters];

				if (didTimeOut) {
					return;
				}
				cancelTimeout();

				responseData?.conversation_starters.map(({ text: responseStarter }) => {
					const index = updatedStarters.indexOf(null);
					const starterAlreadyExists = updatedStarters.some(
						(starter) => starter?.message === responseStarter,
					);

					// If there is a null value in the starters array AND the new starter we get from the backend
					// does not already exist in the array, replace the null value with the new starter
					if (index !== -1 && !starterAlreadyExists) {
						updatedStarters[index] = {
							message: responseStarter,
							type: 'llm-generated',
							labelType: 'llm-generated',
							llmLabel: getLLMLabel().labelText,
						};

						setStarters(updatedStarters);
					}
				});
			},
			onError: (error) => {
				onError?.(error);

				experienceTracker.fail({
					teamName: 'Rovo Chat',
					name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
					error,
				});
			},
		});

		const beginTimeout = useCallback(
			(
				userDefinedStarters: Array<ConversationStarterWithLabel>,
				fallbackStarters: Array<ConversationStarterWithLabel>,
			) => {
				timeoutRef.current = window.setTimeout(() => {
					experienceTracker.abort({
						name: ExperienceName.FETCH_CONTEXTUAL_STARTERS,
						reason: 'timeout',
					});
					setDidTimeOut(true);
					const conversationStarterLimit = getConversationStarterLimit(fullScreen ?? false);
					setStarters(
						[...userDefinedStarters, ...fallbackStarters].slice(0, conversationStarterLimit),
					);
				}, 2000);

				return cancelTimeout;
			},
			[cancelTimeout, experienceTracker, fullScreen],
		);

		return {
			getDynamicContextualStarters,
			starters,
			error,
			// TODO - Implement appropriate request abort logic for useMutation, much like useStreamingMutation has, this is a stop gap
			isMutating: didTimeOut ? false : isSubmitting,
			staticContextualConversationStarters,
			cancelTimeout,
		};
	},
);
